import type { GuidedFlow } from './FAQFlow';

export type HelpArticle = {
  key: string;
  title: string;
  description: string[];
  videoUrl?: string;
  searchKeywords?: string[];
};

export type HelpTopic = {
  key: string;
  title: string;
  summary: string;
  searchKeywords?: string[];
  articles?: HelpArticle[];
  guidedFlows?: GuidedFlow[];
};

const sensorGuidedFlow: GuidedFlow = {
  key: 'sensor-troubleshooting',
  title: 'Find hjælp til en sensor',
  description:
    'Svar på nogle få spørgsmål om typen af sensor og symptomet, så får du de mest sandsynlige næste trin.',
  searchKeywords: ['måling', 'målinger', 'signal', 'device-id', 'GPS', 'GSM', 'niveau', 'flow'],
  startNodeId: 'sensor-type',
  nodes: {
    'sensor-type': {
      id: 'sensor-type',
      type: 'question',
      title: 'Vælg sensortype',
      question: 'Hvilken type sensor eller enhed handler det om?',
      helperText: 'Du behøver ikke kende det tekniske navn. Vælg det der passer bedst.',
      answers: [
        {
          key: 'flow',
          label: 'Niveau / flow sensor',
          description: 'Beholder, tromle, IBC eller flow/niveaumåling.',
          nextNodeId: 'flow-symptom',
        },
        {
          key: 'plant-module',
          label: 'Plant module',
          description: 'Modul til anlæg, alarmer, niveau eller pumpehistorik.',
          nextNodeId: 'flow-symptom',
        },
        {
          key: 'gps',
          label: 'GPS / simtracking',
          description: 'Position, rute eller en enhed der ikke flytter sig på kortet.',
          nextNodeId: 'gps-symptom',
        },
        {
          key: 'gsm',
          label: 'GSM plant / custom',
          description: 'SMS, GSM alarmer eller specialopsætning.',
          nextNodeId: 'gsm-symptom',
        },
      ],
    },
    'flow-symptom': {
      id: 'flow-symptom',
      type: 'question',
      title: 'Niveau eller flow',
      question: 'Hvad oplever du?',
      answers: [
        {
          key: 'no-data',
          label: 'Der kommer ingen nye målinger',
          description: 'Graf eller status er ikke blevet opdateret som forventet.',
          nextNodeId: 'flow-no-data-result',
        },
        {
          key: 'wrong-level',
          label: 'Niveauet ser forkert ud',
          description: 'Målingen virker for høj, for lav eller hopper pludseligt.',
          nextNodeId: 'flow-wrong-level-result',
        },
        {
          key: 'alarm',
          label: 'Der er en alarm',
          description: 'Enheden viser lavt niveau, varme/frost eller generisk alarm.',
          nextNodeId: 'flow-alarm-result',
        },
      ],
    },
    'gps-symptom': {
      id: 'gps-symptom',
      type: 'question',
      title: 'GPS',
      question: 'Hvad er problemet med GPS enheden?',
      answers: [
        {
          key: 'no-position',
          label: 'Positionen opdateres ikke',
          description: 'Kortet viser en gammel placering eller ingen ny aktivitet.',
          nextNodeId: 'gps-no-position-result',
        },
        {
          key: 'wrong-position',
          label: 'Placeringen ser forkert ud',
          description: 'Enheden hopper, står et forkert sted eller har upræcis rute.',
          nextNodeId: 'gps-wrong-position-result',
        },
      ],
    },
    'gsm-symptom': {
      id: 'gsm-symptom',
      type: 'question',
      title: 'GSM',
      question: 'Hvad sker der med GSM enheden?',
      answers: [
        {
          key: 'no-data',
          label: 'Der kommer ingen beskeder eller data',
          description: 'Systemet modtager ikke de forventede GSM/SMS opdateringer.',
          nextNodeId: 'gsm-no-data-result',
        },
        {
          key: 'alarm',
          label: 'Der kommer en alarm',
          description: 'Enheden sender en alarm eller viser en aktiv hændelse.',
          nextNodeId: 'gsm-alarm-result',
        },
      ],
    },
    'flow-no-data-result': {
      id: 'flow-no-data-result',
      type: 'result',
      title: 'Start med strøm, signal og aktiv status',
      summary:
        'Manglende målinger skyldes oftest strøm, netværk/signal eller at sensoren er sat inaktiv.',
      nextSteps: [
        'Tjek at enheden har strøm og at eventuelt batteri ikke er afladet.',
        'Kontroller at enheden står et sted med forbindelse og ikke er flyttet til et afskærmet område.',
        'Hvis du er logget ind, tjek om sensoren står som aktiv og om seneste måling er ældre end forventet.',
      ],
      supportText:
        'Kontakt support med sensorens navn eller device-id, hvis den stadig ikke sender.',
    },
    'flow-wrong-level-result': {
      id: 'flow-wrong-level-result',
      type: 'result',
      title: 'Kontroller montering og beholdertype',
      summary:
        'Uventede niveauer kommer ofte fra ændret montering, forkert beholdertype eller en fysisk ændring i beholderen.',
      nextSteps: [
        'Se om sensoren sidder lige og har fri afstand til væske, låg, slanger eller kanter.',
        'Tjek at beholdertypen passer til opsætningen, for eksempel tromle eller IBC.',
        'Sammenlign med den seneste manuelle måling, hvis der findes en.',
      ],
      supportText: 'Send gerne et billede af monteringen sammen med sensorens navn.',
    },
    'flow-alarm-result': {
      id: 'flow-alarm-result',
      type: 'result',
      title: 'Aflæs alarmen som en driftshændelse først',
      summary:
        'Alarmer kan være reelle driftshændelser, men de kan også udløses af grænseværdier eller hurtige niveauskift.',
      nextSteps: [
        'Tjek om niveau, temperatur eller flow faktisk er uden for normal drift.',
        'Se om alarmen er ny, gentagende eller allerede løst.',
        'Hvis alarmen virker forkert, noter tidspunktet og hvad sensoren viste fysisk.',
      ],
      supportText: 'Support kan hjælpe med at vurdere grænseværdier og alarmhistorik.',
    },
    'gps-no-position-result': {
      id: 'gps-no-position-result',
      type: 'result',
      title: 'Tjek signal, strøm og seneste aktivitet',
      summary:
        'GPS opdaterer typisk ikke hvis enheden mangler strøm, står uden signal eller ikke har været i bevægelse.',
      nextSteps: [
        'Kontroller at enheden har strøm og er tændt.',
        'Flyt enheden til et sted med friere udsyn, hvis den står indendørs eller under tag.',
        'Tjek om enheden forventes at sende ved bevægelse, interval eller begge dele.',
      ],
      supportText: 'Oplys gerne seneste kendte placering og hvornår den sidst flyttede sig.',
    },
    'gps-wrong-position-result': {
      id: 'gps-wrong-position-result',
      type: 'result',
      title: 'Upræcis GPS skyldes ofte omgivelserne',
      summary:
        'Bygninger, metal, indendørs placering og kortvarigt dårligt signal kan give hop eller upræcise punkter.',
      nextSteps: [
        'Sammenlign flere punkter over tid i stedet for kun en enkelt måling.',
        'Tjek om enheden har været indendørs, tæt på høje bygninger eller i et køretøj.',
        'Hvis afvigelsen er stor og gentagende, noter tidspunkterne for de forkerte punkter.',
      ],
      supportText: 'Send eksempler på tidspunkter og forventet placering ved fejlsøgning.',
    },
    'gsm-no-data-result': {
      id: 'gsm-no-data-result',
      type: 'result',
      title: 'Tjek SIM, dækning og beskedflow',
      summary:
        'Når GSM data mangler, er SIM-kort, dækning, strøm eller ændrede beskedformater de mest almindelige årsager.',
      nextSteps: [
        'Kontroller at enheden har strøm og at SIM-kortet er aktivt.',
        'Tjek om der er mobildækning på placeringen.',
        'Hvis der er ændret på alarmtekst eller opsætning, kan beskederne kræve justering.',
      ],
      supportText: 'Gem gerne den rå SMS/alarmtekst, hvis du har adgang til den.',
    },
    'gsm-alarm-result': {
      id: 'gsm-alarm-result',
      type: 'result',
      title: 'Vurder alarmteksten og den fysiske status',
      summary:
        'GSM alarmer bør først sammenholdes med anlæggets fysiske status og den besked enheden sender.',
      nextSteps: [
        'Læs alarmteksten og kontroller om anlægget faktisk står i samme tilstand.',
        'Se om alarmen er gentaget flere gange eller kun sendt en enkelt gang.',
        'Noter tidspunkt, alarmtekst og anlæggets fysiske status.',
      ],
      supportText: 'Support kan hjælpe med at matche alarmtekst til den rigtige hændelse.',
    },
  },
};

export const helpTopics: HelpTopic[] = [
  {
    key: 'sensors',
    title: 'Sensorer',
    summary: 'Guidet hjælp til sensorer, GPS, GSM og niveau-/flowmålinger.',
    searchKeywords: [
      'måler',
      'måling',
      'målinger',
      'device',
      'device-id',
      'GPS',
      'GSM',
      'niveau',
      'flow',
    ],
    guidedFlows: [sensorGuidedFlow],
  },
  {
    key: 'tasks',
    title: 'Opgaver',
    summary: 'Guides til opgaveskabeloner, planlægning og ændringer i eksisterende opgaver.',
    searchKeywords: ['task', 'tasks', 'template', 'schedule', 'tjekpunkt', 'dokumentation'],
    articles: [
      {
        key: 'tasks-template',
        title: 'Hvordan opretter man en opgave (skabelon)',
        description: [
          'Du kan oprette en opgave skabelon som kan bruges flere gange:',
          '1. Du kan oprette et eller flere tjekpunkter',
          '2. Du kan tilføje instruktioner til hvert tjekpunkt (tekst, billede, video m.fl.)',
          '3. Du kan kræve dokumentation for at opgaven kan blive markeret som fuldført',
          '4. Du kan flytte rundt på rækkefølgen af tjekpunkter',
        ],
        searchKeywords: ['skabelon', 'tjekpunkt', 'dokumentation'],
        videoUrl:
          'https://storage.googleapis.com/akstotal-v2.appspot.com/Guides/tasks/Opret-opgave-skabelon.mp4',
      },
      {
        key: 'tasks-schedule',
        title: 'Hvordan planlægger man en opgave?',
        description: ['Vælg en opgaveskabelon og planlæg den på den ønskede dato.'],
        searchKeywords: ['planlægning', 'dato', 'schedule'],
        videoUrl:
          'https://storage.googleapis.com/akstotal-v2.appspot.com/Guides/tasks/Planl%C3%A6gge-en-opgave.mp4',
      },
      {
        key: 'tasks-update-existing-schedule',
        title: 'Hvordan opretter jeg en opgave der allerede er planlagt?',
        description: [
          'Åbn den planlagte opgave, lav ændringen og gem, så den fremtidige opgave følger den nye opsætning.',
        ],
        searchKeywords: ['ændring', 'eksisterende', 'planlagt'],
        videoUrl:
          'https://storage.googleapis.com/akstotal-v2.appspot.com/Guides/tasks/Opdater-en-allerede-planlagt-opgave.mp4',
      },
      {
        key: 'tasks-move-schedule',
        title:
          'Hvordan flytter jeg en opgave til en anden dato eller interval som allerede er planlagt?',
        description: [
          'Find den planlagte opgave, vælg ny dato eller interval, og gem ændringen for de relevante forekomster.',
        ],
        searchKeywords: ['flyt', 'dato', 'interval'],
        videoUrl:
          'https://storage.googleapis.com/akstotal-v2.appspot.com/Guides/tasks/rykke-opgave-til-anden-dato.mp4',
      },
    ],
  },
  {
    key: 'overview',
    title: 'Overblik',
    summary: 'Hjælp til dashboard, årshjul og visninger på tværs af afdelinger.',
    searchKeywords: ['dashboard', 'årshjul', 'print', 'overview'],
    articles: [
      {
        key: 'print-overview',
        title: 'Hvordan printer jeg årshjulet?',
        description: [
          '1. Gå til dashboardet for din afdeling',
          '2. Klik på Årshjul',
          '3. Klik på Print',
        ],
        searchKeywords: ['print', 'årshjul', 'dashboard'],
        videoUrl:
          'https://storage.googleapis.com/akstotal-v2.appspot.com/Guides/print-a%CC%8Arshjul.mp4',
      },
    ],
  },
];
